import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Area, AreaChart, Cell
} from "recharts";
import { useTranslation } from "react-i18next";
import {
  Activity, Upload, Database, Search, FileText, AlertTriangle,
  CheckCircle2, XCircle, ChevronDown, ChevronRight, Trash2, Eye,
  Loader2, Plus, X, Filter, Beaker, Shield, TrendingUp, Target,
  BarChart3, ArrowRight
} from "lucide-react";

const SEVERITY_STYLES = {
  critical: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", icon: XCircle },
  warning: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", icon: AlertTriangle },
  pass: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", icon: CheckCircle2 },
};

const SentinelPage = () => {
  const { t } = useTranslation(['modules', 'common']);
  const [activeTab, setActiveTab] = useState("scan");
  const [uploads, setUploads] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showSpecModal, setShowSpecModal] = useState(false);
  const [analyzeForm, setAnalyzeForm] = useState({ column: "", usl: "", lsl: "", subgroup_size: 1, analysis_name: "" });
  const [specForm, setSpecForm] = useState({ name: "", parameter: "", usl: "", lsl: "", unit: "", product: "", region: "Global", standard_ref: "", notes: "" });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, aRes, sRes, stRes] = await Promise.all([
        fetch(`${API}/sentinel/uploads?limit=50`, { credentials: "include" }),
        fetch(`${API}/sentinel/analyses?limit=50`, { credentials: "include" }),
        fetch(`${API}/sentinel/specs`, { credentials: "include" }),
        fetch(`${API}/sentinel/stats`, { credentials: "include" }),
      ]);
      if (uRes.ok) { const d = await uRes.json(); setUploads(d.uploads || []); }
      if (aRes.ok) { const d = await aRes.json(); setAnalyses(d.analyses || []); }
      if (sRes.ok) { const d = await sRes.json(); setSpecs(d.specs || []); }
      if (stRes.ok) setStats(await stRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/sentinel/upload`, { method: "POST", credentials: "include", body: fd });
      if (res.ok) {
        const data = await res.json();
        setSelectedUpload(data);
        setAnalyzeForm(prev => ({ ...prev, column: data.numeric_columns?.[0] || "" }));
        fetchAll();
      }
    } catch (e) { console.error(e); }
    setUploading(false);
    e.target.value = "";
  };

  const viewUpload = async (uploadId) => {
    try {
      const res = await fetch(`${API}/sentinel/uploads/${uploadId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSelectedUpload(data);
        setAnalyzeForm(prev => ({ ...prev, column: data.numeric_columns?.[0] || "" }));
        setActiveTab("scan");
      }
    } catch (e) { console.error(e); }
  };

  const runAnalysis = async () => {
    if (!selectedUpload || !analyzeForm.column) return;
    setAnalyzing(true);
    try {
      const body = {
        upload_id: selectedUpload.upload_id,
        column: analyzeForm.column,
        analysis_name: analyzeForm.analysis_name,
        subgroup_size: parseInt(analyzeForm.subgroup_size) || 1,
      };
      if (analyzeForm.usl) body.usl = parseFloat(analyzeForm.usl);
      if (analyzeForm.lsl) body.lsl = parseFloat(analyzeForm.lsl);
      const res = await fetch(`${API}/sentinel/analyze`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(body),
      });
      if (res.ok) {
        const result = await res.json();
        setSelectedAnalysis(result);
        setActiveTab("results");
        fetchAll();
      }
    } catch (e) { console.error(e); }
    setAnalyzing(false);
  };

  const viewAnalysis = async (analysisId) => {
    try {
      const res = await fetch(`${API}/sentinel/analyses/${analysisId}`, { credentials: "include" });
      if (res.ok) {
        setSelectedAnalysis(await res.json());
        setActiveTab("results");
      }
    } catch (e) { console.error(e); }
  };

  const deleteUpload = async (id) => {
    if (!window.confirm("Delete this upload and all related analyses?")) return;
    await fetch(`${API}/sentinel/uploads/${id}`, { method: "DELETE", credentials: "include" });
    if (selectedUpload?.upload_id === id) setSelectedUpload(null);
    fetchAll();
  };

  const deleteAnalysis = async (id) => {
    if (!window.confirm(t('modules:sentinel.deleteAnalysisConfirm'))) return;
    await fetch(`${API}/sentinel/analyses/${id}`, { method: "DELETE", credentials: "include" });
    if (selectedAnalysis?.analysis_id === id) setSelectedAnalysis(null);
    fetchAll();
  };

  const saveSpec = async () => {
    try {
      const body = { ...specForm };
      if (body.usl) body.usl = parseFloat(body.usl);
      if (body.lsl) body.lsl = parseFloat(body.lsl);
      await fetch(`${API}/sentinel/specs`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(body),
      });
      setShowSpecModal(false);
      setSpecForm({ name: "", parameter: "", usl: "", lsl: "", unit: "", product: "", region: "Global", standard_ref: "", notes: "" });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const deleteSpec = async (id) => {
    if (!window.confirm("Delete this specification?")) return;
    await fetch(`${API}/sentinel/specs/${id}`, { method: "DELETE", credentials: "include" });
    fetchAll();
  };

  const tabs = [
    { id: "scan", label: t('modules:sentinel.tabScan'), icon: Activity },
    { id: "results", label: t('modules:sentinel.tabResults'), icon: BarChart3 },
    { id: "specs", label: t('modules:sentinel.tabSpecs'), icon: Target },
    { id: "history", label: t('modules:sentinel.tabHistory'), icon: Database },
  ];

  if (loading) {
    return <QMSLayout title={t('modules:sentinel.title')}><div className="flex items-center justify-center h-64" data-testid="sentinel-loading"><Loader2 className="w-8 h-8 animate-spin text-[#C9A84C]" /></div></QMSLayout>;
  }

  return (
    <QMSLayout title={t('modules:sentinel.title')}>
      <div className="space-y-6" data-testid="sentinel-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Activity className="w-6 h-6 text-[#C9A84C]" /> {t('modules:sentinel.pageTitle')}
            </h1>
            <p className="text-sm text-[#8B95A5] mt-1">{t('modules:sentinel.pageSubtitle')}</p>
          </div>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sentinel-stats">
            <StatCard label={t('modules:sentinel.uploads')} value={stats.total_uploads} />
            <StatCard label={t('modules:sentinel.analyses')} value={stats.total_analyses} />
            <StatCard label={t('modules:sentinel.specifications')} value={stats.total_specs} />
            <StatCard label={t('modules:sentinel.critical')} value={stats.callout_summary?.critical || 0} color="text-red-400" />
            <StatCard label={t('modules:sentinel.warnings')} value={stats.callout_summary?.warning || 0} color="text-amber-400" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-[#181B23] p-1 rounded-xl border border-[rgba(255,255,255,0.05)] w-fit">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${activeTab === tab.id ? "bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30" : "text-[#8B95A5] hover:text-white border border-transparent"}`}>
              <tab.icon className="w-3.5 h-3.5" /> {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ INTELLIGENCE SCAN TAB ═══ */}
        {activeTab === "scan" && (
          <div className="space-y-4">
            {/* Upload Area */}
            <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-6" data-testid="upload-zone">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#C9A84C]" /> {t('modules:sentinel.dataIngestion')}
                </h2>
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/30 hover:bg-[#C9A84C]/20 transition-colors" data-testid="upload-btn">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {uploading ? t('common:status.uploading') : t('modules:sentinel.uploadData')}
                  <input type="file" accept=".csv,.xlsx,.json" className="hidden" onChange={handleUpload} disabled={uploading} />
                </label>
              </div>

              {selectedUpload ? (
                <div className="space-y-4">
                  <div className="bg-[#111318] rounded-lg p-4 border border-[rgba(255,255,255,0.05)]">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-xs font-mono text-[#C9A84C]">{selectedUpload.upload_id}</span>
                        <h3 className="text-sm text-white font-medium">{selectedUpload.filename}</h3>
                        <p className="text-[10px] text-[#8B95A5]">{selectedUpload.row_count} rows | {selectedUpload.headers?.length} columns | {selectedUpload.numeric_columns?.length} numeric</p>
                      </div>
                      <span className="text-[10px] text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded border border-emerald-400/20">{t('modules:sentinel.immutable')}</span>
                    </div>

                    {/* Data Preview */}
                    {selectedUpload.data && selectedUpload.data.length > 0 && (
                      <div className="overflow-x-auto max-h-48 overflow-y-auto mb-3">
                        <table className="w-full text-[10px]">
                          <thead><tr className="border-b border-[rgba(255,255,255,0.05)]">
                            {selectedUpload.headers?.slice(0, 8).map(h => (
                              <th key={h} className={`px-2 py-1 text-left font-medium ${selectedUpload.numeric_columns?.includes(h) ? "text-[#C9A84C]" : "text-[#8B95A5]"}`}>{h}</th>
                            ))}
                          </tr></thead>
                          <tbody>
                            {selectedUpload.data.slice(0, 10).map((row, i) => (
                              <tr key={i} className="border-b border-[rgba(255,255,255,0.03)]">
                                {selectedUpload.headers?.slice(0, 8).map(h => (
                                  <td key={h} className="px-2 py-1 text-white">{String(row[h] ?? "")}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Analysis Configuration */}
                  <div className="bg-[#111318] rounded-lg p-4 border border-[rgba(255,255,255,0.05)]">
                    <h3 className="text-xs font-semibold text-white mb-3 flex items-center gap-1.5">
                      <Beaker className="w-3.5 h-3.5 text-[#C9A84C]" /> {t('modules:sentinel.configureAnalysis')}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div>
                        <label className="text-[10px] text-[#8B95A5] block mb-1">{t('modules:sentinel.dataColumn')}</label>
                        <select value={analyzeForm.column} onChange={e => setAnalyzeForm(p => ({ ...p, column: e.target.value }))}
                          className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none" data-testid="select-column">
                          <option value="">{t('modules:sentinel.selectColumn')}</option>
                          {selectedUpload.numeric_columns?.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-[#8B95A5] block mb-1">{t('modules:sentinel.usl')}</label>
                        <input type="number" step="any" value={analyzeForm.usl} onChange={e => setAnalyzeForm(p => ({ ...p, usl: e.target.value }))}
                          className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none" data-testid="input-usl" placeholder="e.g. 11.0" />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#8B95A5] block mb-1">{t('modules:sentinel.lsl')}</label>
                        <input type="number" step="any" value={analyzeForm.lsl} onChange={e => setAnalyzeForm(p => ({ ...p, lsl: e.target.value }))}
                          className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none" data-testid="input-lsl" placeholder="e.g. 9.0" />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#8B95A5] block mb-1">{t('modules:sentinel.subgroupSize')}</label>
                        <input type="number" min="1" max="25" value={analyzeForm.subgroup_size} onChange={e => setAnalyzeForm(p => ({ ...p, subgroup_size: e.target.value }))}
                          className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none" data-testid="input-subgroup" />
                      </div>
                      <div>
                        <label className="text-[10px] text-[#8B95A5] block mb-1">{t('modules:sentinel.analysisName')}</label>
                        <input value={analyzeForm.analysis_name} onChange={e => setAnalyzeForm(p => ({ ...p, analysis_name: e.target.value }))}
                          className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none" data-testid="input-analysis-name" placeholder="PQ-Batch-001" />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      {specs.length > 0 && (
                        <select onChange={e => {
                          const sp = specs.find(s => s.spec_id === e.target.value);
                          if (sp) setAnalyzeForm(p => ({ ...p, usl: sp.usl || "", lsl: sp.lsl || "" }));
                        }} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none" data-testid="select-spec">
                          <option value="">{t('modules:sentinel.applySpecTemplate')}</option>
                          {specs.map(s => <option key={s.spec_id} value={s.spec_id}>{s.name} ({s.parameter}): {s.lsl} - {s.usl} {s.unit}</option>)}
                        </select>
                      )}
                      <button onClick={runAnalysis} disabled={analyzing || !analyzeForm.column}
                        className="ml-auto flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-medium bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#D4B85E] transition-colors disabled:opacity-50" data-testid="run-analysis-btn">
                        {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                        {analyzing ? t('modules:sentinel.analyzing') : t('modules:sentinel.executeAnalysis')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Database className="w-10 h-10 text-[#8B95A5] opacity-30 mx-auto mb-3" />
                  <p className="text-sm text-[#8B95A5]">{t('modules:sentinel.uploadPrompt')}</p>
                  <p className="text-[10px] text-[#8B95A5] mt-1">{t('modules:sentinel.uploadHint')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ ANALYSIS RESULTS TAB ═══ */}
        {activeTab === "results" && (
          <div className="space-y-4">
            {selectedAnalysis ? (
              <AnalysisResultView analysis={selectedAnalysis} />
            ) : (
              <div className="space-y-3">
                {analyses.length === 0 ? (
                  <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-12 text-center">
                    <BarChart3 className="w-10 h-10 text-[#8B95A5] opacity-30 mx-auto mb-3" />
                    <p className="text-sm text-[#8B95A5]">{t('modules:sentinel.noAnalyses')}</p>
                  </div>
                ) : (
                  analyses.map(a => (
                    <div key={a.analysis_id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4 flex items-center justify-between hover:border-[rgba(255,255,255,0.15)] transition-colors cursor-pointer" data-testid={`analysis-card-${a.analysis_id}`} onClick={() => viewAnalysis(a.analysis_id)}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#C9A84C]/10 border border-[#C9A84C]/30 shrink-0">
                          <BarChart3 className="w-4 h-4 text-[#C9A84C]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-mono text-[#C9A84C]">{a.analysis_id}</span>
                            {a.callouts?.map((c, i) => {
                              const style = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.warning;
                              return <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded border ${style.bg} ${style.border} ${style.text}`}>{c.type}</span>;
                            })}
                          </div>
                          <p className="text-sm text-white truncate">{a.analysis_name || a.column}</p>
                          <p className="text-[10px] text-[#8B95A5]">{a.data_points} points | {new Date(a.analyzed_at).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {a.capability && <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${a.capability.cpk >= 1.33 ? "bg-emerald-400/10 text-emerald-400" : a.capability.cpk >= 1.0 ? "bg-amber-400/10 text-amber-400" : "bg-red-400/10 text-red-400"}`}>Cpk {a.capability.cpk}</span>}
                        <button onClick={(e) => { e.stopPropagation(); deleteAnalysis(a.analysis_id); }} className="p-1.5 text-[#8B95A5] hover:text-red-400" data-testid={`delete-analysis-${a.analysis_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        <ArrowRight className="w-4 h-4 text-[#8B95A5]" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            {selectedAnalysis && (
              <button onClick={() => setSelectedAnalysis(null)} className="text-xs text-[#C9A84C] hover:underline" data-testid="back-to-list">&larr; {t('modules:sentinel.backToAllAnalyses')}</button>
            )}
          </div>
        )}

        {/* ═══ MASTER SPECS TAB ═══ */}
        {activeTab === "specs" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white">{t('modules:sentinel.masterSpecTable')}</h2>
              <button onClick={() => setShowSpecModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/30 hover:bg-[#C9A84C]/20" data-testid="add-spec-btn"><Plus className="w-3 h-3" /> {t('modules:sentinel.addSpecification')}</button>
            </div>
            {specs.length === 0 ? (
              <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-12 text-center">
                <Target className="w-10 h-10 text-[#8B95A5] opacity-30 mx-auto mb-3" />
                <p className="text-sm text-[#8B95A5]">{t('modules:sentinel.noSpecs')}</p>
              </div>
            ) : (
              <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="specs-table">
                    <thead><tr className="border-b border-[rgba(255,255,255,0.07)]">
                      <th className="px-4 py-3 text-left text-[#8B95A5] font-medium">{t('modules:sentinel.thName')}</th>
                      <th className="px-4 py-3 text-left text-[#8B95A5] font-medium">{t('modules:sentinel.thParameter')}</th>
                      <th className="px-4 py-3 text-right text-[#8B95A5] font-medium">{t('modules:sentinel.thLsl')}</th>
                      <th className="px-4 py-3 text-right text-[#8B95A5] font-medium">{t('modules:sentinel.thUsl')}</th>
                      <th className="px-4 py-3 text-left text-[#8B95A5] font-medium">{t('modules:sentinel.thUnit')}</th>
                      <th className="px-4 py-3 text-left text-[#8B95A5] font-medium">{t('modules:sentinel.thRegion')}</th>
                      <th className="px-4 py-3 text-left text-[#8B95A5] font-medium">{t('modules:sentinel.thStandard')}</th>
                      <th className="px-4 py-3 text-right text-[#8B95A5] font-medium">{t('modules:sentinel.thActions')}</th>
                    </tr></thead>
                    <tbody>
                      {specs.map(s => (
                        <tr key={s.spec_id} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]" data-testid={`spec-row-${s.spec_id}`}>
                          <td className="px-4 py-2.5 text-white font-medium">{s.name}</td>
                          <td className="px-4 py-2.5 text-[#C9A84C] font-mono">{s.parameter}</td>
                          <td className="px-4 py-2.5 text-right text-white">{s.lsl ?? "—"}</td>
                          <td className="px-4 py-2.5 text-right text-white">{s.usl ?? "—"}</td>
                          <td className="px-4 py-2.5 text-[#8B95A5]">{s.unit}</td>
                          <td className="px-4 py-2.5 text-[#8B95A5]">{s.region}</td>
                          <td className="px-4 py-2.5 text-[#8B95A5]">{s.standard_ref}</td>
                          <td className="px-4 py-2.5 text-right"><button onClick={() => deleteSpec(s.spec_id)} className="text-[#8B95A5] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══ UPLOAD HISTORY TAB ═══ */}
        {activeTab === "history" && (
          <div className="space-y-3">
            {uploads.length === 0 ? (
              <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-12 text-center">
                <Database className="w-10 h-10 text-[#8B95A5] opacity-30 mx-auto mb-3" />
                <p className="text-sm text-[#8B95A5]">{t('modules:sentinel.noUploads')}</p>
              </div>
            ) : (
              uploads.map(u => (
                <div key={u.upload_id} className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4 flex items-center justify-between" data-testid={`upload-card-${u.upload_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-400/10 border border-emerald-400/30 shrink-0">
                      <FileText className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <span className="text-xs font-mono text-[#C9A84C]">{u.upload_id}</span>
                      <p className="text-sm text-white">{u.filename}</p>
                      <p className="text-[10px] text-[#8B95A5]">{u.row_count} rows | {u.headers?.length} columns | {u.format?.toUpperCase()} | {new Date(u.uploaded_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => viewUpload(u.upload_id)} className="px-3 py-1.5 rounded-lg text-xs bg-[#C9A84C]/10 text-[#C9A84C] border border-[#C9A84C]/30 hover:bg-[#C9A84C]/20" data-testid={`view-upload-${u.upload_id}`}><Eye className="w-3.5 h-3.5 inline mr-1" />{t('modules:sentinel.analyze')}</button>
                    <button onClick={() => deleteUpload(u.upload_id)} className="p-1.5 text-[#8B95A5] hover:text-red-400" data-testid={`delete-upload-${u.upload_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ═══ SPEC MODAL ═══ */}
        {showSpecModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" data-testid="spec-modal">
            <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.1)] rounded-2xl w-full max-w-lg mx-4">
              <div className="p-5 border-b border-[rgba(255,255,255,0.07)] flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">Add Specification</h2>
                <button onClick={() => setShowSpecModal(false)} className="p-1 text-[#8B95A5] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <ModalInput label={t('modules:sentinel.specName')} value={specForm.name} onChange={v => setSpecForm(p => ({ ...p, name: v }))} testId="spec-name" placeholder="Tensile Strength - Lot A" />
                  <ModalInput label={t('modules:sentinel.specParameter')} value={specForm.parameter} onChange={v => setSpecForm(p => ({ ...p, parameter: v }))} testId="spec-param" placeholder="tensile_strength" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <ModalInput label="LSL" value={specForm.lsl} onChange={v => setSpecForm(p => ({ ...p, lsl: v }))} testId="spec-lsl" type="number" placeholder="9.0" />
                  <ModalInput label="USL" value={specForm.usl} onChange={v => setSpecForm(p => ({ ...p, usl: v }))} testId="spec-usl" type="number" placeholder="11.0" />
                  <ModalInput label={t('modules:sentinel.specUnit')} value={specForm.unit} onChange={v => setSpecForm(p => ({ ...p, unit: v }))} testId="spec-unit" placeholder="N/mm2" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <ModalInput label={t('modules:sentinel.specProduct')} value={specForm.product} onChange={v => setSpecForm(p => ({ ...p, product: v }))} testId="spec-product" />
                  <ModalInput label={t('modules:sentinel.specRegion')} value={specForm.region} onChange={v => setSpecForm(p => ({ ...p, region: v }))} testId="spec-region" />
                  <ModalInput label={t('modules:sentinel.specStandardRef')} value={specForm.standard_ref} onChange={v => setSpecForm(p => ({ ...p, standard_ref: v }))} testId="spec-stdref" placeholder="ISO 10993" />
                </div>
              </div>
              <div className="p-5 border-t border-[rgba(255,255,255,0.07)] flex justify-end gap-3">
                <button onClick={() => setShowSpecModal(false)} className="px-4 py-2 rounded-lg text-sm text-[#8B95A5] hover:text-white">{t('common:actions.cancel')}</button>
                <button onClick={saveSpec} disabled={!specForm.name || !specForm.parameter}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#D4B85E] disabled:opacity-50" data-testid="save-spec-btn">{t('modules:sentinel.saveSpecification')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

/* ─── Sub-components ─── */

const StatCard = ({ label, value, color = "text-white" }) => (
  <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg p-3 text-center">
    <p className={`font-bold text-xl ${color}`}>{value}</p>
    <p className="text-[#8B95A5] text-[10px]">{label}</p>
  </div>
);

const ModalInput = ({ label, value, onChange, testId, type = "text", placeholder = "" }) => (
  <div>
    <label className="text-[10px] text-[#8B95A5] block mb-1">{label}</label>
    <input type={type} step="any" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#C9A84C] focus:outline-none" data-testid={testId} />
  </div>
);

const AnalysisResultView = ({ analysis }) => {
  const a = analysis;
  return (
    <div className="space-y-4" data-testid="analysis-result-view">
      {/* Header */}
      <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <span className="text-xs font-mono text-[#C9A84C]">{a.analysis_id}</span>
            <h2 className="text-lg font-semibold text-white">{a.analysis_name || a.column}</h2>
            <p className="text-[10px] text-[#8B95A5]">{a.data_points} data points | Column: {a.column} | {new Date(a.analyzed_at).toLocaleString()}</p>
          </div>
          {a.capability && (
            <div className={`text-center px-4 py-2 rounded-lg border ${a.capability.cpk >= 1.33 ? "bg-emerald-400/10 border-emerald-400/30" : a.capability.cpk >= 1.0 ? "bg-amber-400/10 border-amber-400/30" : "bg-red-400/10 border-red-400/30"}`}>
              <p className={`text-2xl font-bold ${a.capability.cpk >= 1.33 ? "text-emerald-400" : a.capability.cpk >= 1.0 ? "text-amber-400" : "text-red-400"}`}>{a.capability.cpk}</p>
              <p className="text-[10px] text-[#8B95A5]">Cpk</p>
            </div>
          )}
        </div>
      </div>

      {/* Compliance Call-outs */}
      {a.callouts?.length > 0 && (
        <div className="space-y-2" data-testid="callouts-section">
          <h3 className="text-xs font-semibold text-white flex items-center gap-1.5"><Shield className="w-3.5 h-3.5 text-[#C9A84C]" /> {t('modules:sentinel.complianceCallouts')}</h3>
          {a.callouts.map((c, i) => {
            const style = SEVERITY_STYLES[c.severity] || SEVERITY_STYLES.warning;
            const Icon = style.icon;
            return (
              <div key={i} className={`${style.bg} border ${style.border} rounded-lg p-3 flex items-start gap-3`} data-testid={`callout-${c.type}`}>
                <Icon className={`w-4 h-4 ${style.text} shrink-0 mt-0.5`} />
                <div>
                  <p className={`text-xs font-medium ${style.text}`}>{c.message}</p>
                  <p className="text-[10px] text-[#8B95A5] mt-0.5">Action: {c.action}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Narrative */}
      {a.narrative && (
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid="narrative-section">
          <h3 className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#C9A84C]" /> {t('modules:sentinel.analysisNarrative')}</h3>
          <p className="text-xs text-[#8B95A5] leading-relaxed">{a.narrative}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Mean" value={a.descriptive?.mean} />
        <MiniStat label="Std Dev" value={a.descriptive?.std_dev} />
        <MiniStat label="Min" value={a.descriptive?.min} />
        <MiniStat label="Max" value={a.descriptive?.max} />
        <MiniStat label="Median" value={a.descriptive?.median} />
        <MiniStat label="Variance" value={a.descriptive?.variance} />
        <MiniStat label="Range" value={a.descriptive?.range} />
        <MiniStat label="Count" value={a.descriptive?.count} />
      </div>

      {/* Capability Details */}
      {a.capability && (
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid="capability-section">
          <h3 className="text-xs font-semibold text-white mb-3">{t('modules:sentinel.processCapability')}</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <MiniStat label="Cp" value={a.capability.cp} />
            <MiniStat label="Cpk" value={a.capability.cpk} color={a.capability.cpk >= 1.33 ? "text-emerald-400" : "text-red-400"} />
            <MiniStat label="Pp" value={a.capability.pp} />
            <MiniStat label="Ppk" value={a.capability.ppk} color={a.capability.ppk >= 1.33 ? "text-emerald-400" : "text-red-400"} />
            <MiniStat label="CPU" value={a.capability.cpu} />
            <MiniStat label="CPL" value={a.capability.cpl} />
          </div>
          <p className="text-[10px] text-[#8B95A5] mt-2">{a.capability.message}</p>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Histogram */}
        {a.histogram && (
          <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid="histogram-chart">
            <h3 className="text-xs font-semibold text-white mb-3">{t('modules:sentinel.histogramTitle')}</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={a.histogram.bin_centers.map((c, i) => ({ center: c, count: a.histogram.counts[i], normal: a.histogram.normal_curve[i] }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="center" tick={{ fontSize: 9, fill: "#8B95A5" }} tickFormatter={v => v.toFixed(2)} />
                <YAxis tick={{ fontSize: 9, fill: "#8B95A5" }} />
                <Tooltip contentStyle={{ background: "#1F222D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 10 }} />
                <Bar dataKey="count" fill="#C9A84C" opacity={0.7} radius={[2, 2, 0, 0]} name="Frequency">
                  {a.histogram.bin_centers.map((_, i) => (
                    <Cell key={i} fill={
                      a.specs?.usl && a.histogram.bin_centers[i] > a.specs.usl ? "#ef4444" :
                      a.specs?.lsl && a.histogram.bin_centers[i] < a.specs.lsl ? "#ef4444" : "#C9A84C"
                    } />
                  ))}
                </Bar>
                <Line type="monotone" dataKey="normal" stroke="#4ADE80" strokeWidth={2} dot={false} name="Normal Fit" />
                {a.specs?.usl && <ReferenceLine x={a.specs.usl} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "USL", fill: "#ef4444", fontSize: 9 }} />}
                {a.specs?.lsl && <ReferenceLine x={a.specs.lsl} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "LSL", fill: "#ef4444", fontSize: 9 }} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Control Chart */}
        {a.control_chart && a.control_chart.chart_type !== "insufficient_data" && (
          <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid="control-chart">
            <h3 className="text-xs font-semibold text-white mb-3">{a.control_chart.chart_type} Control Chart</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={(a.control_chart.individuals || a.control_chart.subgroup_means || []).map((v, i) => ({ index: i + 1, value: v }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="index" tick={{ fontSize: 9, fill: "#8B95A5" }} />
                <YAxis tick={{ fontSize: 9, fill: "#8B95A5" }} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#1F222D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 10 }} />
                <ReferenceLine y={a.control_chart.center_line || a.control_chart.x_double_bar} stroke="#C9A84C" strokeDasharray="3 3" label={{ value: "CL", fill: "#C9A84C", fontSize: 9 }} />
                <ReferenceLine y={a.control_chart.ucl || a.control_chart.ucl_x} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "UCL", fill: "#ef4444", fontSize: 9 }} />
                <ReferenceLine y={a.control_chart.lcl || a.control_chart.lcl_x} stroke="#ef4444" strokeDasharray="5 5" label={{ value: "LCL", fill: "#ef4444", fontSize: 9 }} />
                <Line type="monotone" dataKey="value" stroke="#38BDF8" strokeWidth={1.5} dot={{ r: 2, fill: "#38BDF8" }} name="Value" />
              </LineChart>
            </ResponsiveContainer>
            {a.control_chart.nelson_violations?.length > 0 && (
              <div className="mt-2 space-y-1">
                {a.control_chart.nelson_violations.map((v, i) => (
                  <p key={i} className="text-[10px] text-red-400">Nelson Rule {v.rule}: {v.desc}</p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Outlier Section */}
      {a.outliers && a.outliers.outlier_count > 0 && (
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid="outlier-section">
          <h3 className="text-xs font-semibold text-white mb-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> {t('modules:sentinel.outliersDetected')} ({a.outliers.outlier_count})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-[rgba(255,255,255,0.07)]">
                <th className="px-3 py-2 text-left text-[#8B95A5]">Index</th>
                <th className="px-3 py-2 text-right text-[#8B95A5]">Value</th>
                <th className="px-3 py-2 text-right text-[#8B95A5]">G Statistic</th>
                <th className="px-3 py-2 text-right text-[#8B95A5]">G Critical</th>
                <th className="px-3 py-2 text-right text-[#8B95A5]">Deviation</th>
              </tr></thead>
              <tbody>
                {a.outliers.outliers.map((o, i) => (
                  <tr key={i} className="border-b border-[rgba(255,255,255,0.03)]">
                    <td className="px-3 py-2 text-white">#{o.index}</td>
                    <td className="px-3 py-2 text-right text-red-400 font-mono">{o.value}</td>
                    <td className="px-3 py-2 text-right text-white">{o.g_statistic}</td>
                    <td className="px-3 py-2 text-right text-[#8B95A5]">{o.g_critical}</td>
                    <td className="px-3 py-2 text-right text-amber-400">{o.deviation_from_mean > 0 ? "+" : ""}{o.deviation_from_mean}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Normality */}
      {a.normality && (
        <div className="bg-[#181B23] border border-[rgba(255,255,255,0.07)] rounded-xl p-4" data-testid="normality-section">
          <h3 className="text-xs font-semibold text-white mb-2">{t('modules:sentinel.normalityTest')}</h3>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <MiniStat label="A-D Statistic" value={a.normality.statistic} />
            <MiniStat label="Critical (5%)" value={a.normality.critical_value_5pct} />
            <MiniStat label="Result" value={a.normality.is_normal ? "NORMAL" : "NON-NORMAL"} color={a.normality.is_normal ? "text-emerald-400" : "text-amber-400"} />
          </div>
          <p className="text-[10px] text-[#8B95A5]">{a.normality.message}</p>
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ label, value, color = "text-white" }) => (
  <div className="bg-[#111318] border border-[rgba(255,255,255,0.05)] rounded-lg p-2 text-center">
    <p className={`text-sm font-medium ${color}`}>{value ?? "—"}</p>
    <p className="text-[9px] text-[#8B95A5]">{label}</p>
  </div>
);

export default SentinelPage;
